/*
Copyright 2026 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

/**
 * Ступень размытия фона. "off" — это отдельное состояние, а не нулевой радиус:
 * при выключенном размытии обработчик кадров вообще не подключается к дорожке.
 */
export type BackgroundBlurLevel = "off" | "weak" | "medium" | "strong";

/**
 * Все ступени в порядке возрастания — в этом же порядке они показываются в
 * настройках.
 */
export const backgroundBlurLevels: readonly BackgroundBlurLevel[] = [
  "off",
  "weak",
  "medium",
  "strong",
];

/**
 * Выше этого значения радиус не имеет смысла: шейдер размытия в
 * @livekit/track-processors сначала делит радиус на downsampleFactor = 4, а
 * потом обрезает результат по MAX_SAMPLES = 16 (см. blurShader.ts и
 * webgl/index.ts в библиотеке). 16 * 4 = 64 — всё, что больше, шейдер молча
 * отбросит, а мы будем считать то же самое.
 */
export const maxUsefulBlurRadius = 64;

/**
 * Радиус размытия для каждой ступени.
 *
 * Откуда числа. Библиотека считает размытие не по полному кадру, а по копии,
 * уменьшенной в 4 раза по каждой стороне (в 16 раз меньше пикселей), и делит
 * переданный радиус на те же 4. То есть шейдер видит sigma = радиус / 4, а
 * число выборок текстуры за проход равно 2 * sigma + 1 (проходов два —
 * горизонтальный и вертикальный). Значит, шаг заметен только кратно четырём:
 *
 *   слабое  8  -> sigma 2 ->  5 выборок за проход
 *   среднее 16 -> sigma 4 ->  9 выборок за проход
 *   сильное 32 -> sigma 8 -> 17 выборок за проход
 *
 * Сильная ступень стоит дороже средней на восемь выборок за проход по кадру
 * в 1/16 площади — цикл в шейдере всё равно фиксированной длины, лишние
 * итерации отбрасываются без чтения текстуры. Поэтому «сильное» не роняет
 * частоту кадров так, как уронил бы честный радиус 32 по полному кадру.
 * Раньше здесь стоял единственный радиус 15 — он давал sigma 3, то есть
 * прежнее «размытие включено» соответствует чему-то между слабым и средним.
 */
export const backgroundBlurRadii: Record<
  Exclude<BackgroundBlurLevel, "off">,
  number
> = {
  weak: 8,
  medium: 16,
  strong: 32,
};

/**
 * Радиус, который на самом деле увидит шейдер размытия: библиотека делит
 * переданное значение на коэффициент уменьшения кадра и обрезает по числу
 * выборок. Нужно, чтобы можно было проверить, что ступени действительно
 * отличаются, а не упираются в один и тот же потолок.
 */
export function effectiveBlurSigma(radius: number): number {
  return Math.min(16, Math.max(1, Math.floor(radius / 4)));
}
